import { DatePipe, NgClass } from '@angular/common';
import { Component, HostListener, TemplateRef, ViewChild, computed, inject, signal } from '@angular/core';

import { LayoutService } from '../../services/layout.service';
import { BaseComponent } from '../base/base.component';
import { LocalStorageService } from '../../../shared/services/local-storage.service';
import { RouterModule } from '@angular/router';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { NgbDropdownModule, NgbModal, NgbModalRef, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NavbarSearchComponent } from '../navbar-search/navbar-search.component';
import { ProfileService } from '../../../shared/services/profile.service';
import { TourService } from '../../tour/tour.service';
import { TourHubComponent } from '../../tour/tour-hub/tour-hub.component';
import { Tour } from '../../tour/tour.model';
import { ActivityEvent, ActivityService } from '../../realtime/activity.service';
import { ActivityGroup, groupActivityByCompany } from '../../realtime/activity-groups';
import { Router } from '@angular/router';
import { GridDensity, UserPreferencesService } from '../../services/user-preferences.service';
import { UserService } from '../../../shared/services/user.service';
import { NotifyService } from '../../../shared/services/notify.service';
import { Tenant } from '../../../shared/models/tenant';
import { switchCompanyAndGo } from '../../../shared/utils/company-switch';
import { CompanySelectComponent } from '../../../shared/components/company-select/company-select.component';
import { GroupBrandService } from '../../services/group-brand.service';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  imports: [DatePipe, NgClass, RouterModule, AvatarComponent, NgbDropdownModule, NgbTooltipModule, NavbarSearchComponent, CompanySelectComponent],
})
export class NavbarComponent extends BaseComponent {
  public pathRedirect: string = '/home';
  public titulo: string = '';
  public nomeUsuario: string = '';
  public avatarUsuario: string = '';

  @ViewChild(NavbarSearchComponent) searchComponent!: NavbarSearchComponent;

  // Tours por módulo respeitam as roles do usuário (mesma régua do menu lateral).
  public moduleTours: Tour[] = [];

  public readonly activity = inject(ActivityService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly notify = inject(NotifyService);

  // Com marca do grupo (cor) a faixa já identifica: no celular a etiqueta do produto sai.
  private readonly groupBrand = inject(GroupBrandService);
  public readonly hasGroupBrand = computed(() => !!this.groupBrand.identity()?.color);

  // Empresas do usuário: o feed traz alertas de todas as filiais do grupo que ele acessa — o nome
  // identifica a de cada alerta, e o clique troca pra ela antes de abrir o registro.
  private readonly companiesById = signal(new Map<number, Tenant>());

  // Painel agrupado por empresa: a ativa primeiro, depois as outras filiais (a de alerta mais
  // recente antes). Só uma empresa com alerta = lista simples, sem cabeçalho.
  public readonly activityGroups = computed<ActivityGroup[]>(() =>
    groupActivityByCompany(this.activity.events(), this.stateService.contextAtual, (id) => this.companyName(id)));

  // Grupos abertos: só o da empresa ativa vem aberto; as outras filiais vêm fechadas (nome +
  // quantidade) e abrem no clique. Guarda só o que o usuário mexeu nesta sessão do painel.
  private readonly toggledGroups = signal(new Set<number>());

  isGroupOpen(group: ActivityGroup): boolean {
    const toggled = this.toggledGroups().has(group.companyId);
    return group.current ? !toggled : toggled;
  }

  // stopPropagation: o ngbDropdown fecha em clique dentro do menu; abrir/fechar um grupo não pode
  // fechar o painel.
  toggleGroup(group: ActivityGroup, event: Event): void {
    event.stopPropagation();
    this.toggledGroups.update((set) => {
      const next = new Set(set);
      if (next.has(group.companyId)) next.delete(group.companyId);
      else next.add(group.companyId);
      return next;
    });
  }

  // Preferências de exibição do usuário (densidade das listagens) — o template lê o signal.
  public readonly preferences = inject(UserPreferencesService);
  public readonly densityOptions: { value: GridDensity; label: string; icon: string }[] = [
    { value: 'normal', label: 'Normal', icon: 'ti-layout-rows' },
    { value: 'compact', label: 'Compacta', icon: 'ti-baseline-density-medium' },
  ];

  constructor(
    private layoutService: LayoutService,
    private localStorageService: LocalStorageService,
    private profileService: ProfileService,
    private tourService: TourService
  ) {
    super();

    this.nomeUsuario = this.localStorageService.getSession()?.name || '';
    this.moduleTours = this.tourService.availableModuleTours();
    // Feed ao vivo: conecta uma vez por sessão (idempotente; sem hub o app segue normal).
    this.activity.start();
    this.subscriptions.add(
      this.userService.GetUserCompanies().subscribe({
        next: (companies) => this.companiesById.set(new Map(companies.map((c) => [c.id, c]))),
        error: () => undefined,
      })
    );

    this.subscriptions.add(
      this.layoutService.titulo$.subscribe((p) => {
        this.titulo = p.nome;
        this.pathRedirect = p.pathRetorno;
      })
    );

    // Foto de perfil: assina o estado compartilhado (atualiza ao vivo após upload/remoção) e
    // dispara o carregamento inicial.
    this.subscriptions.add(
      this.profileService.avatarUrl$.subscribe((url) => {
        this.avatarUsuario = url || '';
      })
    );
    this.profileService.ensureLoaded();
  }

  openSearch() {
    this.searchComponent?.open();
  }

  private readonly modalService = inject(NgbModal);

  startGeneralTour() {
    this.tourService.start(this.tourService.generalTour);
  }

  startTour(tour: Tour) {
    this.tourService.start(tour);
  }

  // Central de tutoriais: progresso, próximo da trilha e todos os tutoriais que o perfil alcança.
  openToursModal() {
    this.modalService.open(TourHubComponent, { size: 'lg', centered: true, scrollable: true, modalDialogClass: 'tour-hub-dialog' });
  }

  openPreferences(template: TemplateRef<unknown>) {
    this.modalService.open(template, { centered: true });
  }

  // ---------- atalhos de teclado ----------
  @ViewChild('shortcutsModal') private shortcutsModalTpl!: TemplateRef<unknown>;
  private shortcutsRef?: NgbModalRef;

  // "?" fora de campos editáveis abre/fecha a lista de atalhos — descobribilidade do Ctrl+K.
  @HostListener('document:keydown', ['$event'])
  onShortcutsKey(event: KeyboardEvent): void {
    if (event.key !== '?' || event.ctrlKey || event.metaKey || event.altKey) return;

    const target = event.target as HTMLElement | null;
    const editable =
      target &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable);
    if (editable) return;

    event.preventDefault();
    this.toggleShortcutsModal();
  }

  toggleShortcutsModal(): void {
    if (this.shortcutsRef) {
      this.shortcutsRef.dismiss();
      return;
    }
    this.shortcutsRef = this.modalService.open(this.shortcutsModalTpl, { centered: true });
    // Zera a ref em qualquer fechamento (esc, backdrop, botão) — o toggle via "?" depende disso.
    const clear = () => {
      this.shortcutsRef = undefined;
    };
    this.shortcutsRef.result.then(clear, clear);
  }

  // Abrir o painel zera o contador; os eventos continuam na lista.
  onActivityOpenChange(open: boolean) {
    if (open) this.activity.markAllRead();
  }

  // Alerta de OUTRA empresa do grupo: abrir o registro com a empresa ativa errada dava "não
  // encontrado" (26/09/2026). Troca pra empresa do alerta e recarrega já no registro.
  openActivity(event: ActivityEvent) {
    if (!event.link) return;
    if (!this.isOtherCompany(event)) {
      this.router.navigateByUrl(event.link);
      return;
    }

    const target = this.companiesById().get(event.companyId);
    if (!target) {
      this.notify.warning('Você não tem mais acesso à empresa deste alerta.');
      return;
    }
    switchCompanyAndGo(this.stateService, target, event.link, (url) => this.reloadAt(url));
  }

  // Recarga cheia (a troca de empresa reconecta o feed e zera o estado da anterior). Separada pro spec.
  protected reloadAt(url: string): void {
    window.location.assign(url);
  }

  isOtherCompany(event: ActivityEvent): boolean {
    const current = this.stateService.contextAtual;
    return !!event.companyId && !!current && event.companyId !== current;
  }

  // Nome curto da empresa do alerta (apelido > razão social); vazio se não estiver na lista.
  activityCompany(event: ActivityEvent): string {
    return this.companyName(event.companyId);
  }

  // Logo do grupo da empresa do alerta; vazio quando o grupo não tem ícone (vale o ícone padrão).
  companyIcon(companyId: number): string {
    return this.companiesById().get(companyId)?.avatarUrl || '';
  }

  private companyName(companyId: number): string {
    const company = this.companiesById().get(companyId);
    return company ? company.fantasyName || company.name : '';
  }

  // Ícone diz QUAL registro falhou (pedido, nota, liquidação); a cor é sempre a de falha — só falha
  // entra no painel.
  activityIcon(type: string): string {
    if (type.startsWith('order-')) return 'ti ti-shopping-cart';
    if (type.startsWith('invoice-')) return 'ti ti-file-invoice';
    if (type.startsWith('payment-')) return 'ti ti-cash';
    if (type.startsWith('integration-')) return 'ti ti-plug-connected-x';
    return 'ti ti-alert-triangle';
  }

  // O que fazer com o alerta — a linha inteira navega pro registro, onde mora a ação (reenviar).
  activityAction(event: ActivityEvent): string {
    if (!event.link) return '';
    const what = event.type.startsWith('order-') ? 'pedido'
      : event.type.startsWith('invoice-') ? 'nota'
      : event.type.startsWith('payment-') ? 'pagamento'
      : event.type.startsWith('integration-') ? 'integração'
      : '';
    // A empresa já está no cabeçalho do grupo: a ação diz só que troca.
    if (this.isOtherCompany(event)) return what ? `Trocar de empresa e abrir ${what}` : 'Trocar de empresa e abrir';
    return what ? `Abrir ${what}` : 'Abrir';
  }

  public sair() {
    this.localStorageService.logout();
  }
}
